import { dropsToXrp } from 'xrpl/dist/npm'
import {
  BITHOMP_API_BASE,
  BITHOMP_API_KEY,
  BITHOMP_API_KEY_HEADER,
  BITHOMP_AMMS_PATH,
  BITHOMP_AMMS_QUERY,
  BITHOMP_AMMS_SEARCH_PATH,
} from './config'
import { XrplCurrency } from './types'

const BITHOMP_RATE_LIMIT_FALLBACK_MS = 30000
const AMM_LIST_TTL_MS = 60 * 1000
const AMM_PAIR_TTL_MS = 15 * 1000
let bithompRateLimitUntil = 0
let ammListInFlight: Promise<BithompAmmSummary[]> | null = null
const ammListCache: { data: BithompAmmSummary[] | null; fetchedAt: number; errorAt: number } = {
  data: null,
  fetchedAt: 0,
  errorAt: 0,
}
const ammPairCache = new Map<
  string,
  { data: BithompAmmInfo | null; fetchedAt: number; errorAt: number; inFlight?: Promise<BithompAmmInfo | null> }
>()

const now = () => Date.now()

const getRetryAfterMs = (response?: Response) => {
  const header = response?.headers?.get('retry-after')
  if (!header) return BITHOMP_RATE_LIMIT_FALLBACK_MS
  const seconds = Number(header)
  if (Number.isFinite(seconds)) {
    return seconds * 1000
  }
  const date = Date.parse(header)
  if (!Number.isNaN(date)) {
    return Math.max(date - now(), BITHOMP_RATE_LIMIT_FALLBACK_MS)
  }
  return BITHOMP_RATE_LIMIT_FALLBACK_MS
}

const setBithompRateLimit = (response?: Response) => {
  const retryAfterMs = getRetryAfterMs(response)
  bithompRateLimitUntil = Math.max(bithompRateLimitUntil, now() + retryAfterMs)
}

const isBithompRateLimited = () => now() < bithompRateLimitUntil

type BithompAmmAmount =
  | string
  | {
      currency: string
      issuer: string
      value: string
    }

export type BithompAmmInfo = {
  ammID: string
  assetA: XrplCurrency
  assetB: XrplCurrency
  reserveA: string
  reserveB: string
  tradingFee: number
  lpToken: {
    currency: string
    issuer: string
  } | null
  lpTokenSupply?: string
  holders?: number
}

export type BithompAmmSummary = {
  ammID: string
  assetA: XrplCurrency
  assetB: XrplCurrency
  reserveA: string
  reserveB: string
  tradingFee: number
  lpToken: {
    currency: string
    issuer: string
  } | null
  lpTokenSupply?: string
  holders?: number
}

const buildHeaders = () => {
  const headers: Record<string, string> = {}
  if (BITHOMP_API_KEY) {
    headers[BITHOMP_API_KEY_HEADER] = BITHOMP_API_KEY
  }
  return headers
}

const toBaseUrl = (path: string, query?: string) => {
  const base = BITHOMP_API_BASE.endsWith('/') ? BITHOMP_API_BASE : `${BITHOMP_API_BASE}/`
  const cleanPath = path.startsWith('/') ? path.slice(1) : path
  if (!query) {
    return `${base}${cleanPath}`
  }
  return `${base}${cleanPath}?${query}`
}

const decodeHexCurrency = (value: string) => {
  if (!/^[A-F0-9]{40}$/i.test(value)) {
    return value
  }
  const chars = []
  for (let i = 0; i < value.length; i += 2) {
    const code = parseInt(value.slice(i, i + 2), 16)
    if (code) {
      chars.push(String.fromCharCode(code))
    }
  }
  const decoded = chars.join('').trim()
  return decoded || value
}

const toXrplAsset = (amount: BithompAmmAmount | null) => {
  if (!amount) return null
  if (typeof amount === 'string') {
    return { currency: 'XRP', value: dropsToXrp(amount) }
  }
  return {
    currency: amount.currency,
    issuer: amount.issuer,
    value: amount.value,
  }
}

const assetMatches = (asset: XrplCurrency, amount: { currency: string; issuer?: string }) => {
  if (asset.isNative || asset.currency === 'XRP') {
    return amount.currency === 'XRP'
  }
  return asset.currency === amount.currency && asset.issuer === amount.issuer
}

const mapAmm = (amm: any, assetA: XrplCurrency, assetB: XrplCurrency): BithompAmmInfo | null => {
  const amount = toXrplAsset(amm?.amount ?? null)
  const amount2 = toXrplAsset(amm?.amount2 ?? null)
  if (!amount || !amount2) return null

  let reserveA = amount
  let reserveB = amount2
  if (assetMatches(assetA, amount2) && assetMatches(assetB, amount)) {
    reserveA = amount2
    reserveB = amount
  }

  if (!assetMatches(assetA, reserveA) || !assetMatches(assetB, reserveB)) {
    return null
  }

  const lpToken = amm?.lpTokenBalance
    ? {
        currency: amm.lpTokenBalance.currency,
        issuer: amm.lpTokenBalance.issuer,
      }
    : null
  const lpTokenSupply = amm?.lpTokenBalance?.value

  return {
    ammID: amm?.ammID,
    assetA: {
      ...assetA,
      symbol: assetA.symbol || decodeHexCurrency(assetA.currency),
    },
    assetB: {
      ...assetB,
      symbol: assetB.symbol || decodeHexCurrency(assetB.currency),
    },
    reserveA: reserveA.value,
    reserveB: reserveB.value,
    tradingFee: typeof amm?.tradingFee === 'number' ? amm.tradingFee : 0,
    lpToken,
    lpTokenSupply,
    holders: amm?.holders,
  }
}

const mapAmmSummary = (amm: any): BithompAmmSummary | null => {
  const amount = toXrplAsset(amm?.amount ?? null)
  const amount2 = toXrplAsset(amm?.amount2 ?? null)
  if (!amount || !amount2) return null

  const assetA: XrplCurrency = {
    currency: amount.currency,
    issuer: amount.issuer,
    symbol: decodeHexCurrency(amount.currency),
    name: decodeHexCurrency(amount.currency),
    decimals: amount.currency === 'XRP' ? 6 : 6,
    isNative: amount.currency === 'XRP',
  }

  const assetB: XrplCurrency = {
    currency: amount2.currency,
    issuer: amount2.issuer,
    symbol: decodeHexCurrency(amount2.currency),
    name: decodeHexCurrency(amount2.currency),
    decimals: amount2.currency === 'XRP' ? 6 : 6,
    isNative: amount2.currency === 'XRP',
  }
  const lpToken = amm?.lpTokenBalance
    ? {
        currency: amm.lpTokenBalance.currency,
        issuer: amm.lpTokenBalance.issuer,
      }
    : null
  const lpTokenSupply = amm?.lpTokenBalance?.value

  return {
    ammID: amm?.ammID,
    assetA,
    assetB,
    reserveA: amount.value,
    reserveB: amount2.value,
    tradingFee: typeof amm?.tradingFee === 'number' ? amm.tradingFee : 0,
    lpToken,
    lpTokenSupply,
    holders: amm?.holders,
  }
}

export const fetchBithompAmms = async () => {
  const timeNow = now()
  if (ammListCache.data && timeNow - ammListCache.fetchedAt < AMM_LIST_TTL_MS) {
    return ammListCache.data
  }
  if (isBithompRateLimited()) {
    return ammListCache.data || []
  }
  if (ammListInFlight) {
    return ammListInFlight
  }

  const url = toBaseUrl(BITHOMP_AMMS_PATH, BITHOMP_AMMS_QUERY)
  const runFetch = async () => {
    if (isBithompRateLimited()) {
      return ammListCache.data || []
    }
    const response = await fetch(url, { headers: buildHeaders() })
    if (response.status === 429) {
      setBithompRateLimit(response)
      return ammListCache.data || []
    }
    if (!response.ok) {
      throw new Error('Failed to fetch AMMs from Bithomp')
    }
    const data = await response.json()
    const list = Array.isArray(data?.amms) ? data.amms : []
    const mapped = list.map(mapAmmSummary).filter((item): item is BithompAmmSummary => Boolean(item))
    ammListCache.data = mapped
    ammListCache.fetchedAt = now()
    ammListCache.errorAt = 0
    return mapped
  }

  ammListInFlight = runFetch()
  try {
    return await ammListInFlight
  } catch (error) {
    ammListCache.errorAt = now()
    if (ammListCache.data) {
      return ammListCache.data
    }
    throw error
  } finally {
    ammListInFlight = null
  }
}

export const fetchBithompAmmByPair = async (assetA: XrplCurrency, assetB: XrplCurrency) => {
  const cacheKey = `${assetA.isNative || assetA.currency === 'XRP' ? 'XRP' : assetA.currency}:${
    assetA.issuer || ''
  }/${assetB.isNative || assetB.currency === 'XRP' ? 'XRP' : assetB.currency}:${assetB.issuer || ''}`
  const cached = ammPairCache.get(cacheKey)
  const timeNow = now()
  if (cached?.data && timeNow - cached.fetchedAt < AMM_PAIR_TTL_MS) {
    return cached.data
  }
  if (cached?.inFlight) {
    return cached.inFlight
  }
  if (isBithompRateLimited()) {
    return cached?.data || null
  }

  const params = new URLSearchParams()
  params.set('currency', assetA.isNative || assetA.currency === 'XRP' ? 'XRP' : assetA.currency)
  if (!assetA.isNative && assetA.currency !== 'XRP' && assetA.issuer) {
    params.set('issuer', assetA.issuer)
  }
  params.set('currency2', assetB.isNative || assetB.currency === 'XRP' ? 'XRP' : assetB.currency)
  if (!assetB.isNative && assetB.currency !== 'XRP' && assetB.issuer) {
    params.set('issuer2', assetB.issuer)
  }

  const url = toBaseUrl(BITHOMP_AMMS_SEARCH_PATH, params.toString())
  const runFetch = async () => {
    const response = await fetch(url, { headers: buildHeaders() })
    if (response.status === 429) {
      setBithompRateLimit(response)
      return cached?.data || null
    }
    if (!response.ok) {
      throw new Error('Failed to fetch AMM by pair')
    }

    const data = await response.json()
    const amm = Array.isArray(data?.amms) ? data.amms[0] : null
    if (!amm) return null
    return mapAmm(amm, assetA, assetB)
  }

  const inFlight = runFetch()
  ammPairCache.set(cacheKey, {
    data: cached?.data || null,
    fetchedAt: cached?.fetchedAt || 0,
    errorAt: cached?.errorAt || 0,
    inFlight,
  })
  try {
    const result = await inFlight
    ammPairCache.set(cacheKey, {
      data: result,
      fetchedAt: now(),
      errorAt: 0,
    })
    return result
  } catch (error) {
    ammPairCache.set(cacheKey, {
      data: cached?.data || null,
      fetchedAt: cached?.fetchedAt || 0,
      errorAt: now(),
    })
    if (cached?.data) {
      return cached.data
    }
    throw error
  }
}
